/**
 * Social features (docs/archive/PLAN-SOCIAL.md): the tunable numbers, in one place (starting values, to adjust
 * with real use; nothing else hard-codes them), the inputs, and the response types of every social
 * route. Those types are the privacy contract: none has a field for anything the visibility matrix keeps
 * private (email, birth year, sex, home area, notes, companions, conditions, difficulty notes, start
 * point and time, GPX, route link, duration, time to summit, SAC, alpine grade, winter, rating, route
 * name), and test/social.test.ts checks every response against that list.
 */
import { z } from "zod/mini";
import type { Activity } from "./schemas";
import type { AscentPhoto } from "./photos";
import type { Logro, SocialStats, Streak } from "./stats";

// ── Competition rules (src/shared/stats.ts) ────────────────────────────────────────────────────────
/** Late logging: an ascent counts on boards and streaks if first stored within this many days after its week. */
export const GRACE_DAYS = 7;
/** Elevation gain counted per ascent on boards ("solo lo que registras"). */
export const GAIN_CAP_M = 5000;
/** One refugio week earned per this many active weeks in the run. */
export const REFUGIO_EVERY = 4;
/** Refugio weeks kept at most. */
export const REFUGIO_MAX = 2;
/** A pause covers 1 to this many weeks from the current one. */
export const PAUSE_MAX_RUN = 12;
/** Paused weeks per calendar (ISO) year. */
export const PAUSE_MAX_PER_YEAR = 12;
/** Logros: summits climbed, weeks in a streak, huellas received. */
export const SUMMIT_LOGROS = [10, 25, 50, 100, 250] as const;
export const STREAK_LOGROS = [4, 10, 26, 52] as const;
export const HUELLAS_LOGRO = 10;
/** "Al día siguiente": others see an ascent dated D from D + 1 at this hour (UTC), once D is over everywhere. */
export const PUBLISH_HOUR_UTC = 12;

// ── People and following ───────────────────────────────────────────────────────────────────────────
/** People one can follow (active or pending); followers are unlimited. */
export const FOLLOW_CAP = 500;
export const HANDLE_MIN = 3;
export const HANDLE_MAX = 20;
/** A handle can change once in this many days; the old one stays held for as long. */
export const HANDLE_COOLDOWN_DAYS = 30;
export const BIO_MAX = 160;
export const COMMENT_MAX = 280;
export const REPORT_NOTE_MAX = 500;
/** Line breaks kept in a bio or a comment; any more become spaces. */
export const MAX_LINE_BREAKS = 2;
/** Search: handle prefix of at least this many characters, at most this many results. */
export const SEARCH_MIN = 3;
export const SEARCH_LIMIT = 20;
/** Rows per page of ascents, the feed and notices. */
export const PAGE_SIZE = 20;
/** The feed reaches this many days back from the newest ascent a page starts at: a bounded read per friend. */
export const FEED_DAYS = 120;
/** Age under which a profile is locked to the most private options (from the birth year). */
export const ADULT_AGE = 18;
/** Distinct reporters that hide a comment, a bio or a photo until it is reviewed. */
export const AUTO_HIDE_REPORTERS = 3;
/** Accounts younger than this cannot comment. */
export const NEW_ACCOUNT_HOURS = 24;
/** Boards shown at most. */
export const BOARD_ROWS = 50;
/** The client's "today" is accepted within this many days of UTC today. */
export const TODAY_SKEW_DAYS = 1;
/** People kept in the persisted cache (the most recently viewed). */
export const PEOPLE_CACHED = 30;

// ── Retos ──────────────────────────────────────────────────────────────────────────────────────────
/**
 * The app's own challenges, a set every month (src/shared/challenges.ts): metres of gain, new summits,
 * summits in different zones (Pyrenees sectors, ranges elsewhere), and summits someone you follow
 * opened the way to ("Tras sus huellas").
 */
export const CHALLENGE_TYPES = ["gain", "new", "zones", "huellas"] as const;
export type ChallengeType = (typeof CHALLENGE_TYPES)[number];
/** What each month's reto asks for. */
export const CHALLENGE_TARGETS: Record<ChallengeType, number> = { gain: 5000, new: 3, zones: 3, huellas: 2 };

// ── Group ascents (docs/archive/PLAN-GROUPS.md §2) ─────────────────────────────────────────────────────────
/** People one ascent can invite, at most (waiting and accepted). */
export const GROUP_MAX = 20;

// ── Avisos on the phone (Web Push) ─────────────────────────────────────────────────────────────────
/** What can be muted, each a group of notice kinds (follow requests and new followers go together). */
export const PUSH_CATEGORIES = ["follows", "piedras", "comments", "huellas", "logros", "invites"] as const;
export type PushCategory = (typeof PUSH_CATEGORIES)[number];
/** Devices one account keeps subscribed at most (the oldest go first). */
export const PUSH_DEVICES_MAX = 10;
/** Participants of an ascent's comments told about a new one, at most. */
export const THREAD_NOTICES_MAX = 20;

/**
 * Caps per user, counted in D1 over the rows each user created (the SOCIAL_RATE_LIMITER binding adds a
 * per-minute cap on search and every write).
 */
export const CAPS = {
  follows: { n: 60, ms: 3_600_000 },
  piedras: { n: 200, ms: 3_600_000 },
  comments: { n: 30, ms: 3_600_000 },
  commentsBurst: { n: 5, ms: 60_000 },
  reports: { n: 20, ms: 86_400_000 },
  invites: { n: 60, ms: 3_600_000 },
} as const;

// ── Inputs ─────────────────────────────────────────────────────────────────────────────────────────

export const SOCIAL_MODES = ["off", "followers", "members"] as const;
export type SocialMode = (typeof SOCIAL_MODES)[number];
export const PUBLISH_DELAYS = ["next_day", "now"] as const;
export const COMMENT_MODES = ["audience", "nobody"] as const;
export const REPORT_REASONS = ["spam", "offensive", "impersonation", "minor_safety", "other"] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const REPORT_KINDS = ["profile", "ascent", "comment", "photo"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/** 3 to 20 of `a-z 0-9 _ .`, starting with a letter. Stored lower case; unique ignoring case. */
export const HANDLE_RE = new RegExp(`^[a-z][a-z0-9_.]{${HANDLE_MIN - 1},${HANDLE_MAX - 1}}$`);

/** PUT /api/social/me: the whole social profile, replaced (idempotent, so the outbox can replay it). */
export const socialMeInput = z.object({
  handle: z.nullable(z.string().check(z.trim(), z.toLowerCase(), z.regex(HANDLE_RE))),
  bio: z.nullable(z.string().check(z.maxLength(BIO_MAX * 2))),
  social: z.enum(SOCIAL_MODES),
  leaderboards: z.boolean(),
  publishDelay: z.enum(PUBLISH_DELAYS),
  zone: z.nullable(z.string().check(z.maxLength(40))),
  comments: z.enum(COMMENT_MODES),
  /** Turning social off: "Borrar también mis comentarios" (on others' ascents). Otherwise they stay. */
  deleteComments: z.optional(z.boolean()),
});
export type SocialMeInput = z.input<typeof socialMeInput>;

/** PUT …/ascents/:ascentId/comments/:commentId: plain text, 1 to COMMENT_MAX characters once cleaned. */
export const commentInput = z.object({ body: z.string().check(z.maxLength(COMMENT_MAX * 4)) });

/** PUT /api/reports/:reportId. `ref`: the ascent id, or `<ascentId>/<commentId>` or `<ascentId>/<photoId>`; none for a profile. */
export const reportInput = z.object({
  userId: z.string().check(z.regex(/^[0-9a-f-]{36}$/i)),
  kind: z.enum(REPORT_KINDS),
  ref: z.nullable(z.string().check(z.maxLength(80))),
  reason: z.enum(REPORT_REASONS),
  note: z.nullable(z.string().check(z.maxLength(REPORT_NOTE_MAX * 2))),
});
export type ReportInput = z.input<typeof reportInput>;

/**
 * PUT /api/invites/:ownerId/:ascentId (accepting): the id of the invitee's own copy, or of an ascent of
 * theirs (the same day, one of the same peaks) that joins the group as it is.
 */
export const inviteAcceptInput = z.object({ id: z.string().check(z.regex(/^[0-9a-f-]{36}$/i)) });

/** PUT /api/notices/read: everything up to this time is read. */
export const noticesReadInput = z.object({ until: z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/)) });

const base64url = (min: number, max: number) => z.string().check(z.regex(new RegExp(`^[A-Za-z0-9_-]{${min},${max}}={0,2}$`)));

/** PUT /api/push/:id: this device's push subscription (PushSubscription.toJSON()), from a known push service. */
export const pushSubscriptionInput = z.object({
  endpoint: z.string().check(z.maxLength(2048), z.regex(/^https:\/\//)),
  // An uncompressed P-256 point (65 bytes) and a 16-byte secret, base64url.
  p256dh: base64url(86, 88),
  auth: base64url(21, 24),
});
export type PushSubscriptionInput = z.input<typeof pushSubscriptionInput>;

/** PUT /api/push/prefs: the categories muted on every device. */
export const pushPrefsInput = z.object({ mute: z.array(z.enum(PUSH_CATEGORIES)).check(z.maxLength(PUSH_CATEGORIES.length)) });

// ── Responses ──────────────────────────────────────────────────────────────────────────────────────

/** GET /api/social/me: the caller's own social settings and counts. */
export type SocialMe = {
  handle: string | null;
  bio: string | null;
  /** Hidden from others after reports, until reviewed (only its owner still sees it). */
  bioHidden: boolean;
  social: SocialMode;
  leaderboards: boolean;
  publishDelay: (typeof PUBLISH_DELAYS)[number];
  zone: string | null;
  comments: (typeof COMMENT_MODES)[number];
  /** Under 18: locked to followers, next-day publishing and exact-handle search. */
  minor: boolean;
  banned: boolean;
  /** Younger than NEW_ACCOUNT_HOURS: cannot comment yet. */
  newAccount: boolean;
  /** When the handle can change again (null: now). */
  handleChangeAt: string | null;
  followers: number;
  following: number;
  requests: number;
  unreadNotices: number;
  /** Paused weeks of the streak. */
  pauses: string[];
};

/** Someone in a list: search, follows, followers, requests, blocks, notices. */
export type PersonRow = { id: string; handle: string | null; name: string; avatarUrl: string | null };

/** The viewer's side of the relation: following them (active), asked (pending), or nothing. */
export type Following = "none" | "pending" | "active";

/** GET /api/people/:userId and /by-handle/:handle: the card anyone in its audience sees. */
export type PersonCard = PersonRow & {
  bio: string | null;
  /** A range id, shown to followers only, when its owner turned it on. */
  zone: string | null;
  followers: number;
  following: number;
  self: boolean;
  /** Viewer → them. */
  relation: Following;
  /** Them → viewer ("Te sigue"). */
  followsYou: boolean;
  /** Following needs their approval (a followers-only profile). */
  approval: boolean;
  canSeePassport: boolean;
};

export type FollowRow = PersonRow & { status: "pending" | "active" };

/** A stamp in someone else's passport. Custom peaks are anonymous: no name, place or id. */
export type PassportStamp =
  | { peakId: string; date: string; activity: Activity }
  | { custom: true; elevation: number | null; date: string; activity: Activity };

export type ListProgress = { id: string; name: string; done: number; total: number };

/**
 * A reto made: its insignia, kept for good. `completedOn` is the date of the ascent that made it (others
 * see the insignia once that ascent is published); null for the few made before it was stored.
 */
export type Insignia = { id: string; type: ChallengeType; month: string; completedAt: string; completedOn: string | null };

/** GET /api/people/:userId/passport */
export type PersonPassport = { stamps: PassportStamp[]; pins: string[]; lists: ListProgress[]; insignias: Insignia[] };

/** GET /api/people/:userId/stats: totals, this year, the streak and logros, as others see them. */
export type PersonStats = SocialStats;
export type { Logro, Streak };

/** A peak in someone else's ascent: an id, or an anonymous custom peak. */
export type SocialPeak = { id: string } | { custom: true; elevation: number | null };

/**
 * Someone else who climbed an ascent with its owner (a group ascent): who, and their own copy of it when
 * the viewer can open it (null otherwise: their profile still can be).
 */
export type GroupMember = PersonRow & { ascentId: string | null };

/** An ascent as friends see it: date, peaks, activity, gain and distance, nothing else. */
export type SocialAscent = {
  id: string;
  date: string;
  activity: Activity;
  peaks: SocialPeak[];
  /** The peaks this ascent first stamped ("Cima nueva"). */
  newPeakIds: string[];
  elevationGain: number | null;
  distanceKm: number | null;
  /** "con track": the ascent has a GPX (never shared). */
  hasTrack: boolean;
  /** Its photo, shown to the ascent's audience (none when hidden after reports, or with SOCIAL_PHOTOS off).
   * Always sent; missing from pages a device persisted before photos existed. */
  photos?: AscentPhoto[];
  /** Piedras on it, and whether the viewer put one. */
  piedras: number;
  piedra: boolean;
  /** Comments the viewer can read. */
  comments: number;
  /**
   * The others in its group the viewer may see: their copy not "Solo para mí", published (members of
   * the group see each other at once), no block either way.
   */
  group: GroupMember[];
};

/** A row of Actividad: one of the people the viewer follows, an ascent of theirs, and what it meant. */
export type FeedItem = SocialAscent & {
  person: PersonRow;
  /** Curated lists it moved on, with the count it reached ("Tresmiles: 13 de 212"; the app has the totals). */
  lists: { id: string; done: number }[];
  /** Logros it earned, and the retos it made (`reto:<id>`). */
  logros: string[];
};

/**
 * GET /api/people/:userId/ascents/:ascentId: one ascent, as Actividad shows it, with the people who put a
 * piedra on it (those the viewer can see, newest first; `piedras` counts everyone).
 */
export type AscentDetail = FeedItem & { piedraBy: PersonRow[] };

/** A comment as the viewer sees it. Its author's handle and picture are there while their card is visible. */
export type Comment = { id: string; author: PersonRow; body: string; createdAt: string; mine: boolean; canDelete: boolean };

/** GET …/ascents/:ascentId/comments: oldest first; whether the owner takes new ones; the ascent's piedras. */
export type Comments = { items: Comment[]; open: boolean; piedras: number; piedra: boolean };

export type Page<T> = { items: T[]; next: string | null };


export const NOTICE_KINDS = ["follow_request", "follow", "accepted", "piedra", "comment", "huella", "logro", "invite", "joined"] as const;
export type NoticeKind = (typeof NOTICE_KINDS)[number];

/** GET /api/notices */
export type Notice = {
  id: string;
  kind: NoticeKind;
  /** Who did it (null for logros). */
  actor: PersonRow | null;
  /**
   * Whose ascent it is about, when it is not the reader's: a comment on an ascent the reader commented on
   * too ("Beto también ha comentado el Aneto de Ana", or the owner answering).
   */
  owner: PersonRow | null;
  /** What it is about: a peak (huellas), an ascent (piedras, comments), a logro. */
  peakId: string | null;
  ascentId: string | null;
  logro: string | null;
  /** A comment's words, while they can be read. */
  text: string | null;
  createdAt: string;
  read: boolean;
};
export type NoticesPage = Page<Notice> & { unread: number };

/**
 * GET /api/invites: an ascent someone invited the viewer to, with what accepting copies (its date,
 * peaks, activity, gain and distance; custom peaks anonymous, and never copied) and who is in it already.
 */
export type Invite = {
  owner: PersonRow;
  ascentId: string;
  date: string;
  activity: Activity;
  peaks: SocialPeak[];
  elevationGain: number | null;
  distanceKm: number | null;
  group: GroupMember[];
  createdAt: string;
};

/** GET /api/groups: one's own ascents' companions, and the invitations still waiting, by ascent id. */
export type MyGroups = { members: Record<string, GroupMember[]>; invited: Record<string, PersonRow[]> };

/** GET /api/push: the server's key (null while push is off), what is muted, and how many devices listen. */
export type PushSettings = { publicKey: string | null; mute: PushCategory[]; devices: number };

// ── Boards ─────────────────────────────────────────────────────────────────────────────────────────

/** New summits, active weeks, elevation gain, a curated list's progress, Huellas. Never speed, grade, winter or height. */
export const BOARD_METRICS = ["new", "weeks", "gain", "list", "huellas"] as const;
export type BoardMetric = (typeof BOARD_METRICS)[number];
/** The periods each board offers; `all` only for lists. */
export const BOARD_PERIODS: Record<BoardMetric, readonly BoardPeriod[]> = {
  new: ["week", "month", "year"],
  weeks: ["month", "year"],
  gain: ["week", "month", "year"],
  list: ["year", "all"],
  huellas: ["month", "year"],
};
export type BoardPeriod = "week" | "month" | "year" | "all";

/** A row: the person, their value, and their place (shared on ties; null at zero, never last). */
export type BoardRow = PersonRow & { value: number; place: number | null; self: boolean };

/** GET /api/boards/:metric */
export type Board = {
  metric: BoardMetric;
  period: BoardPeriod;
  /** "2026-W40", "2026-10", "2026", or "all". */
  key: string;
  from: string;
  to: string;
  /** Nothing late can still enter it; until then it reads "Provisional". */
  final: boolean;
  list: { id: string; name: string; total: number } | null;
  rows: BoardRow[];
};

/** Someone the viewer follows in a reto, and how far they have got. */
export type ChallengeFriend = PersonRow & { progress: number; done: boolean };

/** GET /api/challenges: this month's retos. */
export type Challenge = {
  id: string;
  type: ChallengeType;
  target: number;
  from: string;
  to: string;
  joined: boolean;
  /** The viewer's progress while joined, and when they made it (and the date of the ascent that did). */
  progress: number | null;
  completedAt: string | null;
  completedOn: string | null;
  /** Everyone who joined; among them, the people the viewer follows who appear on boards, furthest first. */
  participants: number;
  friends: ChallengeFriend[];
};

/** GET /api/challenges: this month's retos, and every insignia the viewer has earned, newest first. */
export type ChallengesPage = { items: Challenge[]; insignias: Insignia[] };

/**
 * GET /api/boards/cordada: the circle's month as one rope team, and the viewer's own gain in it (null
 * when they stay off the boards, so out of the rope team).
 */
export type Cordada = { key: string; members: number; gain: number; everests: number; progress: number; newSummits: number; mine: number | null };
